import React, { useEffect, useMemo, useState } from 'react';
import { SearchOutlined } from '@ant-design/icons';
import { Alert, Empty, Input, Spin, Typography } from 'antd';

import type { InspectAssessmentUser } from '../../../../state/app/inspect_assessment/types';
import { describeUserActivity } from './pickerOptions';

type Props = {
  users: InspectAssessmentUser[];
  fetching: boolean;
  error?: string;
  selectedKlassId?: number;
  selectedUserId?: number;
  onSelectUser: (userId: number) => void;
  /** Whether the list is narrowed to the selected assessment. */
  assessmentScoped?: boolean;
  /** Whether candidates without an attempt are listed too. */
  showingAll?: boolean;
  /** The quiet line under the heading: how many are listed, how many hidden. */
  summary?: string;
};

const initials = (username: string) => username.slice(0, 2).toUpperCase();

const AssessmentUserSidecar: React.FC<Props> = ({
  users,
  fetching,
  error,
  selectedKlassId,
  selectedUserId,
  onSelectUser,
  assessmentScoped,
  showingAll,
  summary,
}) => {
  const [search, setSearch] = useState('');
  const visibleUsers = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return users;
    return users.filter(user => user.username.toLowerCase().includes(query));
  }, [search, users]);

  useEffect(() => setSearch(''), [selectedKlassId]);

  const klassChosen = selectedKlassId !== undefined;
  const settled = klassChosen && !fetching && !error;
  // Show all only ever puts candidates back when an assessment narrowed the
  // roster in the first place. With no assessment selected the server sends
  // the whole class, so an empty list means the class is empty — offering
  // Show all there promised a remedy that would have changed nothing.
  const emptyKlassDescription =
    showingAll || !assessmentScoped
      ? 'This klass has no candidates.'
      : 'Nobody in this klass has played the selected assessment. Switch on Show all to see every candidate.';

  return (
    <aside
      aria-label="Users in selected klass"
      className="assessment-inspect-assessment__user-sidecar"
    >
      <div className="assessment-inspect-assessment__user-sidecar-heading">
        <span>Users</span>
        {settled && (
          <Typography.Text type="secondary">{users.length}</Typography.Text>
        )}
      </div>

      {settled && summary && (
        <Typography.Text
          className="assessment-inspect-assessment__user-sidecar-summary"
          type="secondary"
        >
          {summary}
        </Typography.Text>
      )}

      {settled && users.length > 0 && (
        <div className="assessment-inspect-assessment__user-search">
          <Input
            allowClear
            aria-label="Search users"
            onChange={event => setSearch(event.target.value)}
            placeholder="Search users"
            prefix={<SearchOutlined />}
            size="small"
            value={search}
          />
        </div>
      )}

      <div className="assessment-inspect-assessment__user-list">
        {fetching ? (
          <div className="assessment-inspect-assessment__centered assessment-inspect-assessment__centered--stacked">
            <Spin size="small" />
            <Typography.Text type="secondary">
              Loading candidates…
            </Typography.Text>
          </div>
        ) : error ? (
          <Alert
            description={error}
            message="Users could not be loaded"
            showIcon
            type="error"
          />
        ) : !klassChosen ? (
          <Empty
            description="Pick a klass to see its candidates and how each one was graded."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : users.length === 0 ? (
          <Empty
            description={emptyKlassDescription}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : visibleUsers.length === 0 ? (
          <Empty
            description="No candidate matches that search."
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          visibleUsers.map(user => {
            const activity = describeUserActivity(user);
            return (
              <button
                className={`assessment-inspect-assessment__user-row ${
                  user.id === selectedUserId
                    ? 'assessment-inspect-assessment__user-row--active'
                    : ''
                }`}
                key={user.id}
                onClick={() => onSelectUser(user.id)}
                type="button"
              >
                <span className="assessment-inspect-assessment__user-initials">
                  {initials(user.username)}
                </span>
                <span className="assessment-inspect-assessment__user-lines">
                  <span className="assessment-inspect-assessment__username">
                    {user.username}
                  </span>
                  {activity && (
                    <span className="assessment-inspect-assessment__user-activity">
                      {activity}
                    </span>
                  )}
                </span>
              </button>
            );
          })
        )}
      </div>
    </aside>
  );
};

export default AssessmentUserSidecar;
